import React from 'react';
import { cn } from '../../utils/formatter';

export interface BadgeProps {
  children: React.ReactNode;
  variant?: 'success' | 'warning' | 'error' | 'danger' | 'info' | 'default';
  style?: 'solid' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  className?: string;
}

export const Badge = ({ 
  children, 
  variant = 'default', 
  style = 'solid',
  size = 'md',
  icon,
  className 
}: BadgeProps) => {
  const getVariantClasses = (variant: string, style: string) => {
    const variants = {
      solid: {
        success: 'text-center text-green-800 dark:text-green-300 border-green-200/50 dark:border-green-700/60',
        warning: 'text-center text-yellow-800 dark:text-yellow-300 border-yellow-200/50 dark:border-yellow-700/60',
        error:   'text-center text-red-800 dark:text-red-300 border-red-200/50 dark:border-red-700/60',
        danger:  'text-center text-red-800 dark:text-red-300 border-red-200/50 dark:border-red-700/60 bg-red-50/10 dark:bg-red-900/10',
        info:    'text-center text-blue-800 dark:text-blue-300 border-blue-200/50 dark:border-blue-700/60',
        default: 'text-center text-gray-800 dark:text-gray-300 border-gray-200/50 dark:border-gray-600/60',
      },
      outline: {
        success: 'text-center text-green-700 dark:text-green-400 border-green-300/70 dark:border-green-500/70',
        warning: 'text-center text-yellow-700 dark:text-yellow-400 border-yellow-300/70 dark:border-yellow-500/70',
        error:   'text-center text-red-700 dark:text-red-400 border-red-300/70 dark:border-red-500/70',
        danger:  'text-center text-red-700 dark:text-red-400 border-red-300/70 dark:border-red-500/70 bg-red-50/10 dark:bg-red-900/10',
        info:    'text-center text-blue-700 dark:text-blue-400 border-blue-300/70 dark:border-blue-500/70',
        default: 'text-center text-gray-700 dark:text-gray-400 border-gray-300/70 dark:border-gray-500/70',
      },
      ghost: {
        success: 'text-center text-green-600 dark:text-green-400 border-transparent',
        warning: 'text-center text-yellow-600 dark:text-yellow-400 border-transparent',
        error:   'text-center text-red-600 dark:text-red-400 border-transparent',
        danger:  'text-center text-red-600 dark:text-red-400 border-transparent bg-red-50/10 dark:bg-red-900/10',
        info:    'text-center text-blue-600 dark:text-blue-400 border-transparent',
        default: 'text-center text-gray-600 dark:text-gray-400 border-transparent',
      },
    };

    return variants[style as keyof typeof variants][variant as keyof typeof variants.solid];
  };

  const getSizeClasses = (size: string, hasIcon: boolean) => {
    const sizes = {
      sm: hasIcon ? 'pl-2 pr-2.5 py-0.5 text-xs gap-1' : 'px-2 py-0.5 text-xs gap-1',
      md: hasIcon ? 'pl-2.5 pr-3 py-1 text-xs gap-1.5' : 'px-2.5 py-1 text-xs gap-1.5',
      lg: hasIcon ? 'pl-3 pr-3.5 py-1.5 text-sm gap-2' : 'px-3 py-1.5 text-sm gap-2',
    };

    return sizes[size as keyof typeof sizes];
  };

  const glassStyle = style !== 'ghost' ? { backdropFilter: 'blur(8px)' } : {};

  return (
    <>
      {/* SVG Filter for tinted glass effect - only render once */}
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <filter id="badge-tinted-glass" x="0%" y="0%" width="100%" height="100%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.003 0.004"
              numOctaves="2"
              seed="7"
              result="noise"
            />
            <feGaussianBlur in="noise" stdDeviation="1.5" result="softNoise" />
            <feDisplacementMap
              in="SourceGraphic"
              in2="softNoise"
              scale="3"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
          <filter id="badge-shimmer" x="0%" y="0%" width="100%" height="100%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="0.5" result="blur" />
            <feSpecularLighting
              in="blur"
              surfaceScale="1"
              specularConstant="0.8"
              specularExponent="20"
              lightingColor="white"
              result="specLight"
            >
              <fePointLight x="-20" y="-20" z="50" />
            </feSpecularLighting>
          </filter>
        </defs>
      </svg>

      <span
        className={cn(
          'inline-flex items-center font-medium rounded-full border',
          'relative overflow-hidden',
          'transition-all duration-250 ease-out',
          // 'hover:shadow-lg hover:scale-[1.02] hover:-translate-y-[0.5px]',
          'active:scale-[0.98] active:translate-y-[0px]',
          // Background gradient adaptável ao tema com sombras
          style !== 'ghost' && 'bg-gradient-to-br from-white/90 via-white/80 to-white/85',
          style !== 'ghost' && 'dark:from-neutral-800/90 dark:via-neutral-700/80 dark:to-neutral-800/85',
          // Sombras adaptadas
          style !== 'ghost' && 'shadow-sm dark:shadow-md',
          style !== 'ghost' && 'shadow-black/5 dark:shadow-black/30',
          getVariantClasses(variant, style),
          getSizeClasses(size, !!icon),
          className
        )}
        style={glassStyle}
      >
        {/* Tinted glass layer with subtle pattern */}
        {style !== 'ghost' && (
          <>
            {/* Main glass highlight - Adaptado ao tema */}
            <div 
              className="absolute inset-0 rounded-full pointer-events-none dark:hidden"
              style={{
                background: `linear-gradient(135deg, 
                  rgba(255,255,255,0.6) 0%, 
                  rgba(255,255,255,0.3) 40%,
                  transparent 70%)`,
                filter: 'url(#badge-tinted-glass)',
                opacity: 0.8
              }}
            />
            <div 
              className="absolute inset-0 rounded-full pointer-events-none hidden dark:block"
              style={{
                background: `linear-gradient(135deg, 
                  rgba(255,255,255,0.12) 0%, 
                  rgba(255,255,255,0.06) 40%,
                  transparent 70%)`,
                filter: 'url(#badge-tinted-glass)',
                opacity: 0.9
              }}
            />
            
            {/* Subtle shimmer effect - Adaptado ao tema */}
            <div 
              className="absolute inset-0 rounded-full pointer-events-none dark:hidden"
              style={{
                background: `radial-gradient(circle at 30% 20%, 
                  rgba(255,255,255,0.5) 0%, 
                  transparent 60%)`,
                filter: 'url(#badge-shimmer)',
                opacity: 0.4
              }}
            />
            <div 
              className="absolute inset-0 rounded-full pointer-events-none hidden dark:block"
              style={{
                background: `radial-gradient(circle at 30% 20%, 
                  rgba(255,255,255,0.15) 0%, 
                  transparent 60%)`,
                filter: 'url(#badge-shimmer)',
                opacity: 0.3
              }}
            />
            
            {/* Border highlight - Adaptado ao tema */}
            <div 
              className="absolute inset-0 rounded-full pointer-events-none dark:hidden"
              style={{
                background: `linear-gradient(135deg, 
                  rgba(255,255,255,0.4) 0%, 
                  transparent 30%,
                  transparent 70%,
                  rgba(255,255,255,0.2) 100%)`,
                opacity: 0.7
              }}
            />
            <div 
              className="absolute inset-0 rounded-full pointer-events-none hidden dark:block"
              style={{
                background: `linear-gradient(135deg, 
                  rgba(255,255,255,0.08) 0%, 
                  transparent 30%,
                  transparent 70%,
                  rgba(255,255,255,0.04) 100%)`,
                opacity: 0.7
              }}
            />
          </>
        )}
        
        {/* Content */}
        <span className="relative z-10 font-medium drop-shadow-sm flex items-center gap-1">
          {icon && (
            <span className="flex-shrink-0 flex items-center justify-center">
              {icon}
            </span>
          )}
          <span className="inline-flex items-center">
            {children}
          </span>
        </span>
      </span>
    </>
  );
};

export default Badge;